import { useEffect, useState } from 'react'
import { ImageIcon } from 'lucide-react'
import { resolveFileUrl } from '../services/api'
import { cardStorageKey, keyFromSignedUrl } from '../utils/cardRef'

/**
 * CardThumb — s110 (TestFlight b77, Dr. Rami: "Images not rendering", then "Now the cards show up…"). A saved card's
 * thumbnail (insurance front/back, secondary, photo ID).
 *
 * WHY. The screens drew `cardUrls[side] || <the card's saved url>`. While useResolvedCardUrls was still signing,
 * and whenever signing failed, that saved url — a presigned link captured at upload time, long expired — went to
 * <img> and iOS drew "?". Now:
 *   - while the fresh link is pending (`resolved === undefined`) a placeholder shows, never the stale link;
 *   - a fresh capture (base64 `data`) shows at once;
 *   - if the image still fails to load, it is signed again ONCE (from its storage key, or the key inside the expired
 *     link, utils/cardRef keyFromSignedUrl), and the placeholder stays if that fails too.
 */
export default function CardThumb({ card, resolved, alt = '', className = 'w-full h-full object-cover' }) {
  const fresh = card && typeof card === 'object' && typeof card.data === 'string' && card.data.startsWith('data:') ? card.data : null
  const [src, setSrc] = useState(resolved || fresh || null)
  const [retried, setRetried] = useState(false)

  useEffect(() => { setSrc(resolved || fresh || null); setRetried(false) }, [resolved, fresh])

  const onError = async () => {
    if (retried) { setSrc(null); return }
    setRetried(true)
    const raw = card && (typeof card === 'string' ? card : card.url)
    const key = cardStorageKey(card) || keyFromSignedUrl(raw)
    if (!key) { setSrc(null); return }
    try {
      const signed = await resolveFileUrl(key)
      setSrc(typeof signed === 'string' && signed !== key && /^(https?:|data:)/i.test(signed) ? signed : null)
    } catch { setSrc(null) }
  }

  if (!src) {
    return (
      <span className="w-full h-full grid place-items-center" style={{ background: '#F4F7FA' }} data-testid="card-thumb-placeholder">
        <ImageIcon size={22} style={{ color: '#A7B8C6' }} />
      </span>
    )
  }
  return <img src={src} alt={alt} className={className} onError={onError} />
}
